import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useNotificationsStore } from './use-notifications-store';

export interface CartItem {
  productId: string;
  productName: string;
  productImage: string;
  skuId: string;
  colorId: string | null;
  width: number;
  colorName: string | null;
  price: number;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  promoCode: string | null;
  promoDiscount: number;
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (
    productId: string,
    skuId: string,
    colorId: string | null
  ) => void;
  updateQuantity: (
    productId: string,
    skuId: string,
    colorId: string | null,
    quantity: number
  ) => void;
  clearCart: () => void;
  totalItems: () => number;
  totalPrice: () => number;
  setPromoCode: (code: string | null, discount: number) => void;
  clearPromoCode: () => void;
}

function findItemIndex(
  items: CartItem[],
  productId: string,
  skuId: string,
  colorId: string | null
): number {
  return items.findIndex(
    (item) =>
      item.productId === productId &&
      item.skuId === skuId &&
      item.colorId === colorId
  );
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      promoCode: null,
      promoDiscount: 0,

      addItem: (item) => {
        set((state) => {
          const existingIndex = findItemIndex(
            state.items,
            item.productId,
            item.skuId,
            item.colorId
          );

          if (existingIndex >= 0) {
            const updated = [...state.items];
            updated[existingIndex] = {
              ...updated[existingIndex],
              quantity: updated[existingIndex].quantity + 1,
            };
            return { items: updated };
          }

          return { items: [...state.items, { ...item, quantity: 1 }] };
        });
        // Fire notification for history (toast is separate, in the component)
        try {
          useNotificationsStore.getState().addNotification({
            type: 'success',
            title: 'Товар добавлен в корзину',
            message: item.productName,
          });
        } catch { /* ignore if store not ready */ }
      },

      removeItem: (productId, skuId, colorId) => {
        const existing = get().items.find(
          (item) =>
            item.productId === productId &&
            item.skuId === skuId &&
            item.colorId === colorId
        );
        set((state) => ({
          items: state.items.filter(
            (item) =>
              !(
                item.productId === productId &&
                item.skuId === skuId &&
                item.colorId === colorId
              )
          ),
        }));
        if (existing) {
          try {
            useNotificationsStore.getState().addNotification({
              type: 'info',
              title: 'Товар удалён из корзины',
              message: existing.productName,
            });
          } catch { /* ignore */ }
        }
      },

      updateQuantity: (productId, skuId, colorId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId, skuId, colorId);
          return;
        }

        set((state) => {
          const updated = state.items.map((item) =>
            item.productId === productId &&
            item.skuId === skuId &&
            item.colorId === colorId
              ? { ...item, quantity }
              : item
          );
          return { items: updated };
        });
      },

      clearCart: () => {
        set({ items: [], promoCode: null, promoDiscount: 0 });
      },

      totalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      totalPrice: () => {
        const raw = get().items.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0
        );
        return Math.max(0, raw - get().promoDiscount);
      },

      setPromoCode: (code, discount) => {
        set({ promoCode: code, promoDiscount: discount });
      },

      clearPromoCode: () => {
        set({ promoCode: null, promoDiscount: 0 });
      },
    }),
    {
      name: 'dveriant-cart',
    }
  )
);
